import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Undo2,
  Loader2,
  ExternalLink,
  X,
} from 'lucide-react';
import { api, parseApiError, type FlagSeverity } from '../lib/api';
import { isAdminRole } from '../lib/roles';
import { useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SEVERITY_ORDER, SEVERITY_META, formatDate, URL_RE, StatusChip } from '../lib/disciplineFlagDisplay';

// ─────────────────────────────────────────────────────────────────────────────
// DisciplineFlagPanel — admin-only severity cards against a player or
// a clan. Labels only (no penalty rulebook, no score effects, no player
// notification) — see src/lambda/portalApi/index.ts "DISCIPLINE FLAGS (v1)".
//
// Org-scoped: pass a fixed `orgId` when the caller already knows it (a clan
// carries its own org); pass `null` to show a small org selector (the player
// case — a player isn't bound to one org).
//
// Severity colours are deliberately NOT the forge accent — blue/amber/red are
// standard Tailwind semantic tones, black is near-black with a light ring so
// it still reads against the dark iron background. Container matches
// OrgMembersPanel/GhostSeatManager: bordered block, no rounded-xl, no glow.
// ─────────────────────────────────────────────────────────────────────────────

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

type Props = {
  targetType: 'player' | 'clan';
  targetId: string;
  targetName?: string;
  orgId?: string | null;
};

export function DisciplineFlagPanel({ targetType, targetId, targetName, orgId: fixedOrgId }: Props) {
  const qc = useQueryClient();

  // ── ALL hooks declared unconditionally, before any early return ──────────
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware copy — the admin gate below reads THIS, not the raw /me
  // role. Issue/co-sign/overturn write permanent discipline records against a
  // real player, so a founder previewing a lower role must lose the whole
  // panel, exactly as that role would. Identity no-op whenever no view-as is
  // active — see lib/viewAs.ts's useEffectiveMe.
  const effectiveMe = useEffectiveMe(me);
  const isAdmin = isAdminRole(effectiveMe?.role);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: api.orgs,
    enabled: isAdmin && !fixedOrgId,
  });
  const orgs = orgsQuery.data ?? [];
  const effectiveOrgId = fixedOrgId ?? selectedOrgId ?? orgs[0]?.id ?? null;

  const [severity, setSeverity] = useState<FlagSeverity | null>(null);
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [formError, setFormError] = useState('');

  const [overturningSk, setOverturningSk] = useState<string | null>(null);
  const [overturnReasonText, setOverturnReasonText] = useState('');
  const [actionError, setActionError] = useState<{ sk: string; msg: string } | null>(null);

  const historyQuery = useQuery({
    queryKey: ['disciplineFlags', effectiveOrgId, targetType, targetId],
    queryFn: () => api.admin.disciplineFlags.list(effectiveOrgId!, { targetType, targetId }),
    enabled: isAdmin && !!effectiveOrgId,
  });

  const invalidateHistory = () => {
    qc.invalidateQueries({ queryKey: ['disciplineFlags'] });
  };

  const issueMut = useMutation({
    mutationFn: () =>
      api.admin.disciplineFlags.issue(effectiveOrgId!, {
        targetType,
        targetId,
        severity: severity!,
        reason: reason.trim(),
        evidence: evidence.trim() || undefined,
      }),
    onSuccess: () => {
      setSeverity(null);
      setReason('');
      setEvidence('');
      setFormError('');
      invalidateHistory();
    },
    onError: (e: Error) => setFormError(parseApiError(e).message || 'Failed to issue flag.'),
  });

  const cosignMut = useMutation({
    mutationFn: (sk: string) => api.admin.disciplineFlags.cosign(effectiveOrgId!, sk),
    onSuccess: () => {
      setActionError(null);
      invalidateHistory();
    },
    onError: (e: Error, sk) => setActionError({ sk, msg: parseApiError(e).message || 'Failed to co-sign.' }),
  });

  const overturnMut = useMutation({
    mutationFn: ({ sk, reason: r }: { sk: string; reason: string }) =>
      api.admin.disciplineFlags.overturn(effectiveOrgId!, sk, r),
    onSuccess: () => {
      setActionError(null);
      setOverturningSk(null);
      setOverturnReasonText('');
      invalidateHistory();
    },
    onError: (e: Error, vars) => setActionError({ sk: vars.sk, msg: parseApiError(e).message || 'Failed to overturn.' }),
  });

  // ── Gate: non-admins see nothing ──────────────────────────────────────────
  if (!isAdmin) return null;

  const noOrgsAvailable = !fixedOrgId && !orgsQuery.isLoading && orgs.length === 0;
  const stillResolvingOrg = !fixedOrgId && orgsQuery.isLoading;
  const flags = [...(historyQuery.data?.flags ?? [])].sort((a, b) => b.createdAt - a.createdAt);

  const canIssue = !!severity && reason.trim().length > 0 && !!effectiveOrgId && !issueMut.isPending;

  return (
    <div className="border border-iron-800/60 bg-iron-950 p-5">
      {/* ── Header ── */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
        <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          Discipline
        </h2>
        {targetName && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-iron-500">
            &middot; {targetName}
          </span>
        )}
        {!historyQuery.isLoading && !!effectiveOrgId && (
          <span className="font-mono text-[10px] font-normal text-iron-500">
            {flags.length} on record
          </span>
        )}
      </div>

      {/* ── Org selector (player case only) ── */}
      {!fixedOrgId && orgs.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <label className="font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
            Org
          </label>
          <select
            value={effectiveOrgId ?? ''}
            onChange={e => setSelectedOrgId(e.target.value)}
            className="input text-sm"
          >
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      )}

      {noOrgsAvailable && (
        <p className="py-4 text-center font-mono text-[11px] uppercase tracking-wider text-iron-500">
          You need an org to record flags.
        </p>
      )}

      {stillResolvingOrg && (
        <div className="flex items-center gap-2 py-4 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading orgs…</span>
        </div>
      )}

      {!!effectiveOrgId && (
        <>
          {/* ── Issue form ── */}
          <div className="mb-5 space-y-3 border border-forge-900/40 bg-iron-950 p-4">
            <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
              Issue a flag
            </p>

            <div>
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Severity <span className="text-forge-400">*</span>
              </label>
              <div className="flex gap-2">
                {SEVERITY_ORDER.map(sv => {
                  const meta = SEVERITY_META[sv];
                  const selected = severity === sv;
                  return (
                    <button
                      key={sv}
                      type="button"
                      onClick={() => setSeverity(sv)}
                      className={cn(
                        'flex-1 border px-2 py-2 text-center transition-colors',
                        selected
                          ? 'border-forge-500 bg-iron-900'
                          : 'border-iron-700/60 bg-iron-950 hover:border-iron-600'
                      )}
                    >
                      <span className={cn('mb-1.5 block h-3 w-full', meta.swatch)} />
                      <span className="font-display text-[9px] font-black uppercase tracking-wider text-iron-300">
                        {sv}
                      </span>
                    </button>
                  );
                })}
              </div>
              {(severity === 'red' || severity === 'black') && (
                <p className="mt-2 flex items-start gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400/90">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.5} />
                  {SEVERITY_META[severity].label} flags need a second admin's co-sign before they take effect.
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Reason <span className="text-forge-400">*</span>
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="What happened…"
                rows={3}
                className="input w-full resize-none"
              />
            </div>

            <div>
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Evidence <span className="font-normal normal-case tracking-normal text-iron-500">(optional — URL or note)</span>
              </label>
              <input
                value={evidence}
                onChange={e => setEvidence(e.target.value)}
                placeholder="https://discord.com/channels/… or a short note"
                className="input w-full"
              />
            </div>

            {formError && (
              <div className="flex items-center gap-2 border border-red-900/40 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                {formError}
              </div>
            )}

            <button
              type="button"
              onClick={() => issueMut.mutate()}
              disabled={!canIssue}
              className="flex items-center gap-1.5 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {issueMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
              ) : (
                <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
              Issue
            </button>
          </div>

          {/* ── History ── */}
          <div>
            <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.22em] text-iron-500">
              History
            </p>

            {historyQuery.isLoading ? (
              <div className="flex items-center gap-2 py-6 text-iron-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : flags.length === 0 ? (
              <p className="py-4 text-center font-mono text-[11px] uppercase tracking-wider text-iron-500">
                No flags on record.
              </p>
            ) : (
              <div className="space-y-2">
                {flags.map(flag => {
                  const meta = SEVERITY_META[flag.severity];
                  const isOverturned = flag.status === 'overturned';
                  const isPending = flag.status === 'pending';
                  const rowActionError = actionError?.sk === flag.sk ? actionError.msg : null;
                  const isOverturningThis = overturningSk === flag.sk;
                  const cosignBusy = cosignMut.isPending && cosignMut.variables === flag.sk;
                  const overturnBusy = overturnMut.isPending && overturnMut.variables?.sk === flag.sk;

                  return (
                    <div
                      key={flag.sk}
                      className={cn(
                        'border px-3 py-2.5',
                        isOverturned ? 'border-iron-800/50 bg-iron-950 opacity-70' : cn(meta.border, meta.softBg)
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('h-3.5 w-3.5 shrink-0', meta.swatch)} />
                        <span
                          className={cn(
                            'font-display text-[10px] font-black uppercase tracking-wider',
                            isOverturned ? 'text-iron-500' : meta.text
                          )}
                        >
                          {flag.severity}
                        </span>
                        <StatusChip status={flag.status} />
                        <span className="ml-auto font-mono text-[10px] text-iron-500">
                          {formatDate(flag.createdAt)}
                        </span>
                      </div>

                      <p className={cn('mt-1.5 text-sm', isOverturned ? 'text-iron-500 line-through decoration-iron-700' : 'text-iron-100')}>
                        {flag.reason}
                      </p>

                      {flag.evidence && (
                        URL_RE.test(flag.evidence) ? (
                          <a
                            href={flag.evidence}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-forge-300 underline hover:text-forge-200"
                          >
                            Evidence <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                          </a>
                        ) : (
                          <p className="mt-1 font-mono text-[11px] text-iron-500">
                            Evidence: {flag.evidence}
                          </p>
                        )
                      )}

                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-iron-500">
                        Issued by {flag.issuedByName ?? shortId(flag.issuedBy)}
                        {flag.cosignedByName && ` · co-signed by ${flag.cosignedByName}`}
                      </p>

                      {isOverturned && (
                        <p className="mt-1.5 border-t border-iron-800/50 pt-1.5 font-mono text-[10px] text-iron-500">
                          Overturned by {flag.overturnedByName ?? shortId(flag.overturnedBy ?? '')}
                          {flag.overturnReason && <>: <span className="italic">{flag.overturnReason}</span></>}
                        </p>
                      )}

                      {rowActionError && (
                        <div className="mt-2 flex items-center gap-2 border border-red-900/40 bg-red-950/40 px-2.5 py-1.5 text-xs text-red-300">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                          {rowActionError}
                        </div>
                      )}

                      {!isOverturned && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {isPending && (
                            <button
                              type="button"
                              onClick={() => { setActionError(null); cosignMut.mutate(flag.sk); }}
                              disabled={cosignBusy}
                              className="flex items-center gap-1.5 border border-emerald-500/50 bg-emerald-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-900/40 disabled:opacity-40"
                            >
                              {cosignBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
                              )}
                              Co-sign
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setActionError(null);
                              setOverturningSk(isOverturningThis ? null : flag.sk);
                              setOverturnReasonText('');
                            }}
                            className="flex items-center gap-1.5 border border-iron-700 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-iron-500 hover:text-white"
                          >
                            <Undo2 className="h-3 w-3" strokeWidth={2.5} />
                            Overturn
                          </button>
                        </div>
                      )}

                      {isOverturningThis && (
                        <div className="mt-2 border-t border-iron-800/50 pt-2">
                          <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                            Reason for overturning <span className="text-forge-400">*</span>
                          </label>
                          <textarea
                            value={overturnReasonText}
                            onChange={e => setOverturnReasonText(e.target.value)}
                            placeholder="Why is this being overturned…"
                            rows={2}
                            className="input w-full resize-none"
                            autoFocus
                          />
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => { setOverturningSk(null); setOverturnReasonText(''); }}
                              className="flex items-center gap-1 border border-iron-700 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-400 hover:border-iron-500 hover:text-white"
                            >
                              <X className="h-3 w-3" strokeWidth={2.5} />
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => overturnMut.mutate({ sk: flag.sk, reason: overturnReasonText.trim() })}
                              disabled={!overturnReasonText.trim() || overturnBusy}
                              className="flex items-center gap-1.5 border border-red-500/50 bg-red-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-colors hover:border-red-400 hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {overturnBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                              ) : (
                                <Undo2 className="h-3 w-3" strokeWidth={2.5} />
                              )}
                              Confirm overturn
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
